import { MfaRecommendation } from '@/features/auth/security-page';
import { useFeatures } from '@/lib/features';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  Boxes,
  CircleDollarSign,
  ClipboardList,
  TrendingUp,
  UserPlus,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  IconChip,
  Skeleton,
  type Tone,
} from '@/components/ui';
import { PageHeader } from '@/components/page-header';
import { OutboundQueue } from '@/features/processes/outbound-queue';
import { ContractsWatch } from './contracts-watch';
import { PayablesWatch } from './payables-watch';
import { MorningBrief } from './morning-brief';
import { LearnFromDocumentsDialog } from '@/features/learning/learn-dialog';
import { HomeAssistant } from '@/features/assistant/home-assistant';
import { useCanEdit } from '@/lib/auth';
import { PriorityBadge, TaskStatusBadge } from '@/features/tasks/task-status';
import { request } from '@/lib/api';
import { dashboardStatsSchema, taskListSchema } from '@/lib/schemas';
import { cn, formatCurrency, formatNumber, formatRelative } from '@/lib/utils';

export function DashboardPage() {
  const { t } = useTranslation(['dashboard', 'common']);
  const features = useFeatures();
  const canEdit = useCanEdit();
  const stats = useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: ({ signal }) => request('/dashboard/stats', { schema: dashboardStatsSchema, signal }),
  });

  const recent = useQuery({
    queryKey: ['tasks', { recent: true }],
    queryFn: ({ signal }) =>
      request('/tasks?take=8&urgentFirst=true', { schema: taskListSchema, signal }),
  });

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <MfaRecommendation />
      {/* העוזר הוא הליבה — ראשון במסך, לפני המספרים. */}
      <HomeAssistant />
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        action={canEdit ? <LearnFromDocumentsDialog /> : undefined}
      />

      <MorningBrief />
      {features.has('contracts') || features.has('supplierInvoices') ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {features.has('supplierInvoices') ? <PayablesWatch /> : null}
          {features.has('contracts') ? <ContractsWatch /> : null}
        </div>
      ) : null}

      {/* רק כשיש מה לשלוח — ריק לא תופס מקום בסקירה. */}
      <OutboundQueue compact />

      {/* המספר הזה לבדו מצדיק פעולה מיידית, ולכן הוא מעל הכל
          ומופיע רק כשהוא רלוונטי. באנר קבוע נעלם מהעין. */}
      {stats.data && stats.data.overdueUrgent > 0 ? (
        <Link
          to="/tasks"
          className="flex items-center gap-2.5 rounded-(--radius-lg) border border-danger-border bg-danger-subtle px-4 py-3 transition-colors hover:brightness-105"
        >
          <AlertTriangle className="size-4 shrink-0 text-danger" aria-hidden />
          <span className="text-sm text-danger">
            <strong className="tabular font-semibold">{stats.data.overdueUrgent}</strong>{' '}
            {t('urgent.overdue', { count: stats.data.overdueUrgent })}
          </span>
        </Link>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          icon={ClipboardList}
          label={t('stats.openTasks')}
          value={stats.data?.openTasks}
          tone="accent"
          query={stats}
          to="/tasks"
        />
        <Stat
          icon={UserPlus}
          label={t('stats.unassigned')}
          value={stats.data?.unassignedTasks}
          tone={stats.data?.unassignedTasks ? 'warning' : 'violet'}
          query={stats}
          to="/tasks"
        />
        <Stat
          icon={TrendingUp}
          label={t('stats.closedThisMonth')}
          value={stats.data?.closedThisMonth}
          tone="success"
          query={stats}
        />
        {features.has('inventory') ? (
          <Stat
            icon={Boxes}
            label={t('stats.lowStock')}
            value={stats.data?.lowStockItems}
            tone={stats.data?.lowStockItems ? 'danger' : 'teal'}
            query={stats}
            to="/inventory"
          />
        ) : null}
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_16rem]">
        {/* min-w-0: בלי זה פריט grid לא מתכווץ מתחת לרוחב השורה הארוכה, והדף גולש לרוחב בטלפון. */}
        <Card className="min-w-0">
          <CardHeader tone="accent" className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2.5">
              <IconChip icon={ClipboardList} tone="accent" variant="solid" size="sm" />
              {t('recent.title')}
            </CardTitle>
            <Link to="/tasks" className="text-xs font-medium text-accent hover:underline">
              {t('recent.viewAll')}
            </Link>
          </CardHeader>

          {recent.isLoading ? (
            <div className="divide-y divide-border">
              {Array.from({ length: 5 }, (_, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-3.5">
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-5 w-16 rounded-full" />
                  <Skeleton className="h-3 w-20" />
                </div>
              ))}
            </div>
          ) : recent.isError ? (
            <ErrorState error={recent.error} onRetry={() => void recent.refetch()} />
          ) : recent.data?.items.length === 0 ? (
            <EmptyState
              icon={ClipboardList}
              title={t('recent.emptyTitle')}
              description={
                features.has('customers') ? t('recent.emptyWithCustomers') : t('recent.emptyOffice')
              }
            />
          ) : (
            <ul className="divide-y divide-border">
              {recent.data?.items.map((task) => (
                <li key={task.id}>
                  <Link
                    to="/tasks/$taskId"
                    params={{ taskId: task.id }}
                    className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-surface-hover"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-fg">{task.title}</p>
                      <p className="truncate text-xs text-fg-muted">
                        {task.customer?.name ?? '—'}
                        {task.assignedTo
                          ? ` · ${task.assignedTo.name}`
                          : ` · ${t('recent.unassigned')}`}
                      </p>
                    </div>
                    <PriorityBadge priority={task.priority} />
                    <TaskStatusBadge status={task.status} />
                    <time
                      dateTime={task.createdAt}
                      className="hidden w-20 shrink-0 text-start text-xs text-fg-subtle sm:block"
                    >
                      {formatRelative(task.createdAt)}
                    </time>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {features.has('invoicing') ? (
          <Card>
            <CardHeader tone="success">
              <CardTitle className="flex items-center gap-2.5">
                <IconChip icon={CircleDollarSign} tone="success" variant="solid" size="sm" />
                {t('revenue.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats.isLoading ? (
                <Skeleton className="h-8 w-28" />
              ) : (
                <>
                  <p className="tabular text-2xl font-bold text-success">
                    {formatCurrency(stats.data?.revenueThisMonth ?? '0')}
                  </p>
                  <p className="mt-1 text-xs text-fg-subtle">{t('revenue.hint')}</p>
                </>
              )}
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
}

/* פס צבע בצד ה-start של כל כרטיס מדד — זהות המדד במבט אחד (כמו פס הפריט
   הפעיל בסרגל). בלי overflow-hidden על הכרטיס, כדי לא לחתוך את טבעת הפוקוס. */
const STAT_EDGE: Partial<Record<Tone, string>> = {
  accent: 'bg-accent',
  warning: 'bg-warning',
  success: 'bg-success',
  danger: 'bg-danger',
  violet: 'bg-violet',
  teal: 'bg-teal',
};

function Stat({
  icon: Icon,
  label,
  value,
  tone,
  query,
  to,
}: {
  icon: typeof ClipboardList;
  label: string;
  value: number | undefined;
  tone: Tone;
  query: { isLoading: boolean; isError: boolean };
  to?: string;
}) {
  const { t } = useTranslation('common');
  const body = (
    <CardContent className="flex items-center gap-3.5">
      <IconChip icon={Icon} tone={tone} variant="solid" size="lg" />
      <div className="min-w-0">
        <p className="text-xs font-medium text-fg-muted">{label}</p>
        {query.isLoading ? (
          <Skeleton className="mt-1 h-6 w-10" />
        ) : query.isError ? (
          // לא מציגים 0 כשהשליפה נכשלה — אפס שקרי גרוע מ"לא ידוע".
          <p className="text-xl font-semibold text-fg-subtle" title={t('errors.loadFailed')}>
            —
          </p>
        ) : (
          <p className="tabular text-2xl font-bold leading-(--leading-tight) text-fg">
            {formatNumber(value ?? 0)}
          </p>
        )}
      </div>
    </CardContent>
  );

  const edge = (
    <span
      className={cn(
        'pointer-events-none absolute inset-y-4 -start-px w-1 rounded-e-full',
        STAT_EDGE[tone],
      )}
      aria-hidden
    />
  );
  if (!to)
    return (
      <Card className="relative">
        {edge}
        {body}
      </Card>
    );
  return (
    <Card className="relative transition-shadow duration-(--duration-fast) hover:shadow-md">
      {edge}
      <Link to={to} className="block">
        {body}
      </Link>
    </Card>
  );
}
